import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Trash2,
  Save,
  User as UserIcon,
  Building2,
  X,
  Loader2,
  UserPlus,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { contactsApi } from "../../lib/contactsApi";
import { formatApiErrorDetail } from "../../lib/api";
import ContactStatusChip from "./ContactStatusChip";
import AddContactDialog from "./AddContactDialog";

/**
 * Right-side detail drawer. All fields are editable inline; changes are
 * saved via a single "Kaydet" button so we don't hammer the API on every
 * keystroke. `onChanged` is called after successful save/delete so parent
 * lists can refresh.
 */
export default function ContactDetailDrawer({
  open,
  onOpenChange,
  contact,
  statuses = [],
  companies = [],
  people = [],
  users = [],
  teams = [],
  onChanged,
  onCreated,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(contact || {});
  const [saving, setSaving] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [addPersonOpen, setAddPersonOpen] = useState(false);

  useEffect(() => {
    setForm(contact || {});
  }, [contact?.id]);

  if (!contact) return null;

  const isPerson = contact.kind === "person";
  const status = statuses.find((s) => s.id === contact.status_id);
  const linkedCompany = companies.find((c) => c.id === contact.company_id);
  // For companies: derive the list of "authorized persons" by scanning the
  // people prop for anyone whose company_id matches this company. This is
  // the same bidirectional link the create dialog wires up, just displayed.
  const authorizedPersons = !isPerson
    ? people.filter((p) => p.company_id === contact.id)
    : [];
  // Reverse lookup: which internal users have this company in their
  // `assigned_company_ids`, and which team(s) do they belong to? Feeds the
  // "Sorumlular" section so a company opened here shows exactly who from
  // the tenant owns it (mirror of `CopTeams → Yönetilen Şirketler`).
  const ownerUsers = !isPerson
    ? users.filter((u) => (u.assigned_company_ids || []).includes(contact.id))
    : [];
  const ownerRows = ownerUsers.map((u) => ({
    user: u,
    teams: teams.filter((tm) => (tm.member_ids || []).includes(u.id)),
  }));
  const displayName = isPerson
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"
    : contact.name || "—";

  const save = async () => {
    setSaving(true);
    try {
      const patch = { ...form };
      // Coerce empty strings back to null so unset fields don't leak.
      Object.keys(patch).forEach((k) => {
        if (patch[k] === "") patch[k] = null;
      });
      delete patch.id;
      delete patch.tenant_id;
      delete patch.kind;
      delete patch.created_at;
      delete patch.updated_at;
      delete patch.created_by;
      delete patch.owner_user_id;
      const updated = await contactsApi.update(contact.id, patch);
      toast.success(t("contacts.updated_toast"));
      onChanged?.(updated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("contacts.delete_confirm"))) return;
    try {
      await contactsApi.remove(contact.id);
      toast.success(t("contacts.deleted_toast"));
      onOpenChange(false);
      onChanged?.(null, { deleted: contact.id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const addTag = () => {
    const val = tagDraft.trim();
    if (!val) return;
    const tags = form.tags || [];
    if (!tags.includes(val)) {
      setForm({ ...form, tags: [...tags, val] });
    }
    setTagDraft("");
  };
  const removeTag = (tag) =>
    setForm({ ...form, tags: (form.tags || []).filter((x) => x !== tag) });

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-lg"
          data-testid="contact-detail-drawer"
        >
        <SheetHeader className="mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl text-white shadow ${
                isPerson
                  ? "bg-gradient-to-br from-violet-500 to-fuchsia-500"
                  : "bg-gradient-to-br from-sky-500 to-cyan-500"
              }`}
            >
              {isPerson ? <UserIcon className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate text-left" data-testid="contact-detail-title">
                {displayName}
              </SheetTitle>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <ContactStatusChip status={status} />
                {linkedCompany && (
                  <span className="text-xs text-zinc-500">
                    · {linkedCompany.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4">
          {isPerson ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("contacts.field_first_name")}</Label>
                  <Input
                    value={form.first_name || ""}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t("contacts.field_last_name")}</Label>
                  <Input
                    value={form.last_name || ""}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>{t("contacts.field_title")}</Label>
                <Input
                  value={form.title || ""}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <Label>{t("contacts.field_company")}</Label>
                <Select
                  value={form.company_id || "__none__"}
                  onValueChange={(v) =>
                    setForm({ ...form, company_id: v === "__none__" ? "" : v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("contacts.field_company_none")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("contacts.field_company_none")}</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>{t("contacts.field_company_name")}</Label>
                <Input
                  value={form.name || ""}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("contacts.field_website")}</Label>
                  <Input
                    value={form.website || ""}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t("contacts.field_industry")}</Label>
                  <Input
                    value={form.industry || ""}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>{t("contacts.field_address")}</Label>
                <Input
                  value={form.address || ""}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              <div>
                <Label>{t("contacts.field_tax_id")}</Label>
                <Input
                  value={form.tax_id || ""}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("contacts.field_email")}</Label>
              <Input
                type="email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("contacts.field_phone")}</Label>
              <Input
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>{t("contacts.field_status")}</Label>
            <Select
              value={form.status_id || "__none__"}
              onValueChange={(v) =>
                setForm({ ...form, status_id: v === "__none__" ? "" : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("contacts.select_status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("contacts.select_status")}</SelectItem>
                {statuses.filter((s) => s.active !== false).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Yetkili kişiler — company detail view. Persons whose company_id
              matches this company appear as a read-only list, plus a "+ Yeni
              Kişi" button that opens the shared AddContactDialog with the
              company preselected so the new person lands linked on first save. */}
          {!isPerson && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <Label>Yetkili Kişiler ({authorizedPersons.length})</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPersonOpen(true)}
                  data-testid="drawer-add-person-btn"
                  className="h-7 gap-1 border-zinc-300 text-[11px]"
                  title="Bu şirkete bağlı yeni kişi ekle"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  + Yeni Kişi
                </Button>
              </div>
              {authorizedPersons.length === 0 ? (
                <div className="mt-1 rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-500">
                  Bu şirkete bağlı kişi yok. Yukarıdaki &quot;+ Yeni Kişi&quot;
                  ile ekleyebilir veya kişi kartından &quot;Bağlı şirket&quot;
                  olarak bu şirketi atayabilirsin.
                </div>
              ) : (
                <ul className="mt-1 space-y-1 rounded-md border border-zinc-200 bg-white p-1.5">
                  {authorizedPersons.map((p) => (
                    <li
                      key={p.id}
                      data-testid={`drawer-auth-person-${p.id}`}
                      className="flex items-center gap-2 rounded px-2 py-1"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[10px] font-semibold text-white">
                        {(p.first_name || p.last_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <span className="flex-1 truncate text-sm">
                        {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                      </span>
                      {p.email && (
                        <span className="truncate text-[10px] text-zinc-500">{p.email}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Sorumlular — reverse lookup: internal users who have added this
              company to their `assigned_company_ids`, with the teams they
              belong to shown as sub-metadata. Kept read-only here because
              assignment happens from the team drawer's member row (single
              source of truth) — surfacing it here keeps the two sides in
              sync visually without duplicating edit affordances. */}
          {!isPerson && (
            <div>
              <Label>Sorumlular ({ownerUsers.length})</Label>
              {ownerUsers.length === 0 ? (
                <div className="mt-1 rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-500">
                  Bu şirket henüz kimseye atanmamış. Bir takımın üyesine
                  atadığında burada görünecek.
                </div>
              ) : (
                <ul
                  className="mt-1 space-y-1 rounded-md border border-zinc-200 bg-white p-1.5"
                  data-testid="drawer-owners-list"
                >
                  {ownerRows.map(({ user: u, teams: uTeams }) => (
                    <li
                      key={u.id}
                      data-testid={`drawer-owner-${u.id}`}
                      className="flex items-center gap-2 rounded px-2 py-1"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-[10px] font-semibold text-white">
                        {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">{u.name || u.email}</div>
                        {uTeams.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {uTeams.map((tm) => (
                              <span
                                key={tm.id}
                                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                                data-testid={`drawer-owner-team-${tm.id}`}
                              >
                                <span
                                  className="inline-block h-1.5 w-1.5 rounded-full"
                                  style={{ background: tm.color || "#6366f1" }}
                                />
                                {tm.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}


          <div>
            <Label>{t("contacts.field_tags")}</Label>
            <div className="flex flex-wrap items-center gap-1 rounded-md border border-zinc-200 bg-white p-1.5">
              {(form.tags || []).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700 hover:bg-red-100 hover:text-red-700"
                >
                  {tag} ×
                </button>
              ))}
              <input
                className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-1 text-sm outline-none"
                placeholder={t("contacts.field_tags_placeholder")}
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <Label>{t("contacts.field_notes")}</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
            />
          </div>
        </div>

        <div className="sticky bottom-0 mt-6 flex items-center justify-between gap-2 border-t border-zinc-200 bg-white pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={remove}
            data-testid="contact-delete-btn"
            className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            {t("common.delete")}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <X className="mr-1 h-4 w-4" />
              {t("common.close")}
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              data-testid="contact-save-btn"
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t("contacts.save")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

      {/* Sibling AddContactDialog for the "+ Yeni Kişi" shortcut inside the
          company detail drawer. Preselects `defaultCompanyId` so the created
          person is linked in a single save. Uses `onCreated` (not `onChanged`)
          so the parent adds the new person to its `otherKindContacts` list
          without swapping the currently-open company drawer. */}
      {!isPerson && (
        <AddContactDialog
          open={addPersonOpen}
          onOpenChange={setAddPersonOpen}
          initialKind="person"
          companies={companies}
          people={people}
          defaultCompanyId={contact.id}
          onCreated={(created) => onCreated?.(created)}
        />
      )}
    </>
  );
}
